import { useEffect, useRef, useState } from "react";
import { X, Minus, Plus } from "lucide-react";
import { api } from "../api/client";
import { ru } from "../locales/ru";

export const PhotoImage = ({ chatId, fileId, className = "", style, testId }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false, objectUrl;
    setUrl(null); setFailed(false);
    api.get(`/chats/${chatId}/photos/${fileId}`, { responseType: "blob", timeout: 30000 }).then(({ data }) => {
      if (!cancelled) { objectUrl = URL.createObjectURL(data); setUrl(objectUrl); }
    }).catch(err => { if (!cancelled) setFailed(err.response?.status === 404 ? "deleted" : "failed"); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [chatId, fileId, retry]);
  if (failed) return <span data-testid={`${testId}-failed`} className="block p-4 text-xs">{failed === "deleted" ? ru.chats.photoUnavailable : ru.chats.photoFailed}{failed !== "deleted" && <button type="button" data-testid={`${testId}-retry`} onClick={(e) => { e.stopPropagation(); setRetry(retry + 1); }} className="block underline mt-2">{ru.chats.retry}</button>}</span>;
  if (!url) return <span data-testid={`${testId}-loading`} className="block p-4 text-xs animate-pulse">{ru.chats.photoLoading}</span>;
  return <img data-testid={testId} src={url} alt={ru.chats.photo} className={className} style={style} draggable={false} onError={() => setFailed("failed")} />;
};

export const PhotoViewer = ({ message, onClose }) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const closeRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") { e.preventDefault(); const buttons = [...document.querySelectorAll('[data-testid="photo-viewer"] button')]; const i = buttons.indexOf(document.activeElement); buttons[(i + (e.shiftKey ? buttons.length - 1 : 1)) % buttons.length]?.focus(); }
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; previous?.focus(); };
  }, [onClose]);
  const start = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const points = [...pointers.current.values()];
    gesture.current = points.length === 2 ? { distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y), scale } : { x: e.clientX, y: e.clientY, offset };
  };
  const move = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const points = [...pointers.current.values()], g = gesture.current;
    if (points.length === 2 && g.distance) setScale(Math.min(5, Math.max(1, g.scale * Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) / g.distance)));
    else if (points.length === 1 && scale > 1 && g.offset) setOffset({ x: g.offset.x + e.clientX - g.x, y: g.offset.y + e.clientY - g.y });
  };
  const end = (e) => {
    const g = gesture.current;
    if (pointers.current.size === 1 && scale === 1 && g?.y != null && e.clientY - g.y > 100 && Math.abs(e.clientX - g.x) < 100) onClose();
    pointers.current.delete(e.pointerId); gesture.current = {};
  };
  return <div role="dialog" aria-modal="true" aria-label={ru.chats.photo} data-testid="photo-viewer" className="fixed inset-0 z-[80] bg-black text-white flex flex-col">
    <div className="flex items-center gap-4 px-5 py-4 shrink-0">
      <p className="flex-1 truncate" data-testid="viewer-author">{message.sender_name}</p>
      <button data-testid="viewer-zoom-out" aria-label={ru.chats.zoomOut} onClick={() => { setScale(Math.max(1, scale - 0.5)); setOffset({ x: 0, y: 0 }); }}><Minus /></button>
      <button data-testid="viewer-zoom-in" aria-label={ru.chats.zoomIn} onClick={() => setScale(Math.min(5, scale + 0.5))}><Plus /></button>
      <button ref={closeRef} data-testid="viewer-close" aria-label={ru.chats.close} onClick={onClose}><X /></button>
    </div>
    <div data-testid="viewer-gesture-area" className="flex-1 overflow-hidden flex items-center justify-center min-h-0" style={{ touchAction: "none" }} onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={() => { pointers.current.clear(); gesture.current = {}; }} onDoubleClick={() => { setScale(scale === 1 ? 2 : 1); setOffset({ x: 0, y: 0 }); }}>
      <PhotoImage chatId={message.chat_id} fileId={message.photo.file_id} testId="viewer-original" className="max-w-full max-h-full object-contain select-none" style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }} />
    </div>
    <div className="px-5 py-4 text-center"><p data-testid="viewer-caption" className="text-sm whitespace-pre-wrap">{message.text}</p><p className="text-xs text-white/50 mt-2" data-testid="viewer-hint">{ru.chats.viewerHint}</p></div>
  </div>;
};
